import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import MainLayout from "../components/mainLayout";
import "../sass/pages/contasPagar.scss";
import { showError, showSuccess } from "../utils/toast";

/* ===================== HELPERS ===================== */

const STATUS_LABEL = {
  ABERTO: "Aberto",
  PAGO: "Pago",
  PARCIAL: "Parcial",
  CANCELADO: "Cancelado",
};

const STATUS_ORDER = {
  ABERTO: 1,
  PARCIAL: 2,
  PAGO: 3,
  CANCELADO: 4,
};

const FORMA_LABEL = {
  BOLETO: "Boleto",
  PIX: "PIX",
  TED: "TED",
  CARTAO_CREDITO: "Cartão crédito",
  CARTAO_DEBITO: "Cartão débito",
  DINHEIRO: "Dinheiro",
  OUTRO: "Outro",
};

const formatDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("pt-BR");
};

const formatMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const parseMoney = (s) => {
  if (s === "" || s == null) return null;
  const clean = String(s).replace(/\./g, "").replace(",", ".");
  const n = Number(clean);
  return Number.isNaN(n) ? null : n;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const normalizeStr = (val) =>
  (val ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const safeNumber = (val) => {
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
};

const safeDateValue = (val) => {
  if (!val) return 0;
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const SortIcon = ({ active, dir }) => (
  <span
    style={{
      marginLeft: 4,
      fontSize: "0.6rem",
      opacity: active ? 0.9 : 0.4,
    }}
  >
    {!active ? "⇅" : dir === "asc" ? "▲" : "▼"}
  </span>
);

/* -------- agrupamento das parcelas em lançamentos (cabeçalho) -------- */

const agruparParcelasEmLancamentos = (
  parcelas,
  categoriasById = {},
  ccById = {}
) => {
  const map = new Map();

  parcelas.forEach((p) => {
    const cod = p.CODIGO;
    if (!cod) return;

    let lanc = map.get(cod);
    if (!lanc) {
      lanc = {
        codigo: cod,
        descricao: p.DESCRICAO,
        categoriaNome: p.CATEGORIA_NOME || categoriasById[p.ID_CATEGORIA] || "-",
        centroCustoNome: p.CC_NOME || ccById[p.ID_CC] || "-",
        parcelas: [],
      };
      map.set(cod, lanc);
    }
    lanc.parcelas.push(p);
  });

  const lancamentos = [];

  map.forEach((lanc) => {
    const ordenadas = [...lanc.parcelas].sort(
      (a, b) => safeDateValue(a.DT_VENCIMENTO) - safeDateValue(b.DT_VENCIMENTO)
    );

    const qtdParcelas = ordenadas.length;

    const primeiroVencimento = ordenadas[0]?.DT_VENCIMENTO || null;

    const competencias = ordenadas
      .map((p) => p.COMPETENCIA)
      .filter(Boolean)
      .sort();

    let competenciaLabel = "";
    if (competencias.length) {
      const primeira = competencias[0];
      const ultima = competencias[competencias.length - 1];
      competenciaLabel = primeira === ultima ? primeira : `${primeira} a ${ultima}`;
    }

    const valorPrevisto = ordenadas.reduce(
      (acc, p) => acc + Number(p.VALOR_PREVISTO || 0),
      0
    );
    const valorPago = ordenadas.reduce(
      (acc, p) => acc + Number(p.VALOR_PAGO || 0),
      0
    );

    const statusSet = new Set(ordenadas.map((p) => p.STATUS));
    let status;
    if (statusSet.size === 1) {
      status = ordenadas[0].STATUS;
    } else if (statusSet.has("PARCIAL") || statusSet.has("PAGO")) {
      status = "PARCIAL";
    } else if (statusSet.has("CANCELADO") && statusSet.size === 1) {
      status = "CANCELADO";
    } else {
      status = "ABERTO";
    }

    lancamentos.push({
      ...lanc,
      qtdParcelas,
      primeiroVencimento,
      competenciaLabel,
      valorPrevisto,
      valorPago,
      status,
    });
  });

  return lancamentos;
};

/* -------- ordenação dos lançamentos (cabeçalho) -------- */

const sortLancamentos = (lista, field, dir) => {
  const factor = dir === "asc" ? 1 : -1;
  const arr = [...lista];

  arr.sort((a, b) => {
    let va;
    let vb;

    switch (field) {
      case "CODIGO":
        va = normalizeStr(a.codigo);
        vb = normalizeStr(b.codigo);
        break;
      case "PARCELAS":
        va = safeNumber(a.qtdParcelas);
        vb = safeNumber(b.qtdParcelas);
        break;
      case "DESCRICAO":
        va = normalizeStr(a.descricao);
        vb = normalizeStr(b.descricao);
        break;
      case "CATEGORIA":
        va = normalizeStr(a.categoriaNome);
        vb = normalizeStr(b.categoriaNome);
        break;
      case "CC":
        va = normalizeStr(a.centroCustoNome);
        vb = normalizeStr(b.centroCustoNome);
        break;
      case "PRIMEIRO_VENC":
        va = safeDateValue(a.primeiroVencimento);
        vb = safeDateValue(b.primeiroVencimento);
        break;
      case "COMPETENCIA":
        va = normalizeStr(a.competenciaLabel);
        vb = normalizeStr(b.competenciaLabel);
        break;
      case "VALOR_PREVISTO":
        va = safeNumber(a.valorPrevisto);
        vb = safeNumber(b.valorPrevisto);
        break;
      case "VALOR_PAGO":
        va = safeNumber(a.valorPago);
        vb = safeNumber(b.valorPago);
        break;
      case "STATUS":
        va = STATUS_ORDER[a.status] || 99;
        vb = STATUS_ORDER[b.status] || 99;
        break;
      default:
        va = safeDateValue(a.primeiroVencimento);
        vb = safeDateValue(b.primeiroVencimento);
        break;
    }

    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });

  return arr;
};

/* ===================== MODAL CATEGORIAS ===================== */

const CategoriasModal = ({ aberto, onFechar }) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState("");
  const [editId, setEditId] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState("asc");

  const listaOrdenada = useMemo(() => {
    const factor = sortDir === "asc" ? 1 : -1;
    const arr = [...lista];
    arr.sort((a, b) => {
      if (sortField === "id") {
        const va = safeNumber(a.ID_CATEGORIA);
        const vb = safeNumber(b.ID_CATEGORIA);
        return (va - vb) * factor;
      }
      const va = normalizeStr(a.NOME);
      const vb = normalizeStr(b.NOME);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
    return arr;
  }, [lista, sortField, sortDir]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/financeiro/contas-pagar/categorias", {
        params: { page: 1, limit: 500 },
      });
      setLista(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error(e);
      showError("Erro ao carregar categorias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aberto) {
      setNome("");
      setEditId(null);
      load();
    }
  }, [aberto]);

  if (!aberto) return null;

  const handleSalvar = async () => {
    const nomeTrim = nome.trim();
    if (!nomeTrim) {
      showError("Informe o nome da categoria.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/financeiro/contas-pagar/categorias/${editId}`, {
          nome: nomeTrim,
        });
        showSuccess("Categoria atualizada.");
      } else {
        await api.post("/financeiro/contas-pagar/categorias", {
          nome: nomeTrim,
        });
        showSuccess("Categoria criada.");
      }
      setNome("");
      setEditId(null);
      load();
    } catch (e) {
      console.error(e);
      showError(e?.response?.data?.message || "Erro ao salvar categoria.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditar = (cat) => {
    setEditId(cat.ID_CATEGORIA);
    setNome(cat.NOME || "");
  };

  const handleExcluir = async (cat) => {
    if (
      !window.confirm(
        `Confirma excluir a categoria "${cat.NOME}"? (somente se não estiver em uso)`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/financeiro/contas-pagar/categorias/${cat.ID_CATEGORIA}`);
      showSuccess("Categoria excluída.");
      load();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message;
      if (msg === "CATEGORIA_EM_USO") {
        showError("Não é possível excluir: categoria em uso em lançamentos.");
      } else {
        showError("Erro ao excluir categoria.");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return field;
    });
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div className="modal-card">
        <h2>Categorias Financeiras</h2>

        <div className="grid-form">
          <input
            placeholder="Nome da categoria"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
          <button onClick={handleSalvar} disabled={saving}>
            {editId ? "Atualizar" : "Adicionar"}
          </button>
          {editId && (
            <button
              type="button"
              className="outline"
              onClick={() => {
                setEditId(null);
                setNome("");
              }}
            >
              Cancelar edição
            </button>
          )}
        </div>

        {loading ? (
          <p>Carregando categorias...</p>
        ) : (
          <div className="tabela-scroll">
            <table className="tabela-simples">
              <thead>
                <tr>
                  <th
                    onClick={() => toggleSort("id")}
                    style={{ cursor: "pointer" }}
                  >
                    ID
                    <SortIcon active={sortField === "id"} dir={sortDir} />
                  </th>
                  <th
                    onClick={() => toggleSort("nome")}
                    style={{ cursor: "pointer" }}
                  >
                    Nome
                    <SortIcon active={sortField === "nome"} dir={sortDir} />
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {listaOrdenada.map((cat) => (
                  <tr key={cat.ID_CATEGORIA}>
                    <td>{cat.ID_CATEGORIA}</td>
                    <td>{cat.NOME}</td>
                    <td>
                      <button onClick={() => handleEditar(cat)}>Editar</button>
                      <button
                        className="outline danger"
                        onClick={() => handleExcluir(cat)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
                {!listaOrdenada.length && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center" }}>
                      Nenhuma categoria cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-actions">
          <button className="outline" onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== MODAL CENTRO DE CUSTO ===================== */

const CentrosCustoModal = ({ aberto, onFechar }) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState("");
  const [editId, setEditId] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState("asc");

  const listaOrdenada = useMemo(() => {
    const factor = sortDir === "asc" ? 1 : -1;
    const arr = [...lista];
    arr.sort((a, b) => {
      if (sortField === "id") {
        const va = safeNumber(a.ID_CC);
        const vb = safeNumber(b.ID_CC);
        return (va - vb) * factor;
      }
      const va = normalizeStr(a.NOME);
      const vb = normalizeStr(b.NOME);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
    return arr;
  }, [lista, sortField, sortDir]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        "/financeiro/contas-pagar/centros-custo",
        { params: { page: 1, limit: 500 } }
      );
      setLista(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      console.error(e);
      showError("Erro ao carregar centros de custo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aberto) {
      setNome("");
      setEditId(null);
      load();
    }
  }, [aberto]);

  if (!aberto) return null;

  const handleSalvar = async () => {
    const nomeTrim = nome.trim();
    if (!nomeTrim) {
      showError("Informe o nome do centro de custo.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/financeiro/contas-pagar/centros-custo/${editId}`, {
          nome: nomeTrim,
        });
        showSuccess("Centro de custo atualizado.");
      } else {
        await api.post("/financeiro/contas-pagar/centros-custo", {
          nome: nomeTrim,
        });
        showSuccess("Centro de custo criado.");
      }
      setNome("");
      setEditId(null);
      load();
    } catch (e) {
      console.error(e);
      showError(
        e?.response?.data?.message || "Erro ao salvar centro de custo."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEditar = (cc) => {
    setEditId(cc.ID_CC);
    setNome(cc.NOME || "");
  };

  const handleExcluir = async (cc) => {
    if (
      !window.confirm(
        `Confirma excluir o centro de custo "${cc.NOME}"? (somente se não estiver em uso)`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/financeiro/contas-pagar/centros-custo/${cc.ID_CC}`);
      showSuccess("Centro de custo excluído.");
      load();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message;
      if (msg === "CC_EM_USO") {
        showError("Não é possível excluir: centro de custo em uso em lançamentos.");
      } else {
        showError("Erro ao excluir centro de custo.");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return field;
    });
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div className="modal-card">
        <h2>Centros de Custo</h2>

        <div className="grid-form">
          <input
            placeholder="Nome do centro de custo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
          <button onClick={handleSalvar} disabled={saving}>
            {editId ? "Atualizar" : "Adicionar"}
          </button>
          {editId && (
            <button
              type="button"
              className="outline"
              onClick={() => {
                setEditId(null);
                setNome("");
              }}
            >
              Cancelar edição
            </button>
          )}
        </div>

        {loading ? (
          <p>Carregando centros de custo...</p>
        ) : (
          <div className="tabela-scroll">
            <table className="tabela-simples">
              <thead>
                <tr>
                  <th
                    onClick={() => toggleSort("id")}
                    style={{ cursor: "pointer" }}
                  >
                    ID
                    <SortIcon active={sortField === "id"} dir={sortDir} />
                  </th>
                  <th
                    onClick={() => toggleSort("nome")}
                    style={{ cursor: "pointer" }}
                  >
                    Nome
                    <SortIcon active={sortField === "nome"} dir={sortDir} />
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {listaOrdenada.map((cc) => (
                  <tr key={cc.ID_CC}>
                    <td>{cc.ID_CC}</td>
                    <td>{cc.NOME}</td>
                    <td>
                      <button onClick={() => handleEditar(cc)}>Editar</button>
                      <button
                        className="outline danger"
                        onClick={() => handleExcluir(cc)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
                {!listaOrdenada.length && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center" }}>
                      Nenhum centro de custo cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-actions">
          <button className="outline" onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== MODAL NOVO LANÇAMENTO ===================== */

const NovoLancamentoModal = ({
  aberto,
  onFechar,
  categorias,
  centrosCusto,
  onSalvou,
}) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    descricao: "",
    fornecedorId: "",
    categoriaId: "",
    centroCustoId: "",
    tipoDocumento: "",
    numDocumento: "",
    competencia: "",
    dtVencimento: "",
    valor: "",
    formaPagamento: "",
    observacao: "",
  });

  const [parcelamento, setParcelamento] = useState({
    parcelado: false,
    qtdParcelas: "4",
    carenciaDias: "30",
    intervaloDias: "30",
  });

  useEffect(() => {
    if (aberto) {
      setForm({
        descricao: "",
        fornecedorId: "",
        categoriaId: "",
        centroCustoId: "",
        tipoDocumento: "",
        numDocumento: "",
        competencia: "",
        dtVencimento: "",
        valor: "",
        formaPagamento: "",
        observacao: "",
      });
      setParcelamento({
        parcelado: false,
        qtdParcelas: "4",
        carenciaDias: "30",
        intervaloDias: "30",
      });
    }
  }, [aberto]);

  const parcelasPreview = useMemo(() => {
    if (
      !parcelamento.parcelado ||
      !form.dtVencimento ||
      !form.valor ||
      !Number(parcelamento.qtdParcelas)
    )
      return null;

    const qtd = Number(parcelamento.qtdParcelas);
    const vTotal = parseMoney(form.valor) || 0;
    if (!qtd || !vTotal) return null;
    const vParcela = vTotal / qtd;

    return {
      qtd,
      valorParcela: vParcela,
    };
  }, [parcelamento, form.dtVencimento, form.valor]);

  if (!aberto) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleParcelamentoChange = (e) => {
    const { name, checked, value, type } = e.target;
    setParcelamento((p) => ({
      ...p,
      [name]:
        type === "checkbox"
          ? checked
          : value.replace(/\D/g, "").slice(0, 3),
    }));
  };

  const handleSalvar = async () => {
    const {
      descricao,
      fornecedorId,
      categoriaId,
      centroCustoId,
      tipoDocumento,
      numDocumento,
      competencia,
      dtVencimento,
      valor,
      formaPagamento,
      observacao,
    } = form;

    if (!descricao.trim()) {
      showError("Descrição é obrigatória.");
      return;
    }
    if (!categoriaId) {
      showError("Selecione a categoria.");
      return;
    }
    if (!competencia) {
      showError("Competência é obrigatória.");
      return;
    }
    if (!dtVencimento) {
      showError("Data de vencimento é obrigatória.");
      return;
    }
    const valorNum = parseMoney(valor);
    if (!valorNum || valorNum <= 0) {
      showError("Valor inválido.");
      return;
    }

    if (parcelamento.parcelado) {
      const qtd = Number(parcelamento.qtdParcelas);
      if (!qtd || qtd < 2) {
        showError("Informe a quantidade de parcelas (mínimo 2).");
        return;
      }
    }

    const payloadBase = {
      descricao: descricao.trim(),
      fornecedorId: fornecedorId ? Number(fornecedorId) : null,
      categoriaId: Number(categoriaId),
      centroCustoId: centroCustoId ? Number(centroCustoId) : null,
      tipoDocumento: tipoDocumento || null,
      numDocumento: numDocumento || null,
      competencia,
      dtVencimento,
      valor: valorNum,
      formaPagamento: formaPagamento || null,
      observacao: observacao || null,
    };

    setSaving(true);
    try {
      if (parcelamento.parcelado) {
        const qtdParcelas = Math.max(
          2,
          Number(parcelamento.qtdParcelas) || 0
        );
        const carenciaDias = Number(parcelamento.carenciaDias) || 0;
        const intervaloDias = Number(parcelamento.intervaloDias) || 30;

        await api.post("/financeiro/contas-pagar/parcelado", {
          ...payloadBase,
          qtdParcelas,
          carenciaDias,
          intervaloDias,
        });
      } else {
        await api.post("/financeiro/contas-pagar", payloadBase);
      }

      showSuccess("Lançamento criado.");
      onSalvou?.();
      onFechar();
    } catch (e) {
      console.error(e);
      showError(e?.response?.data?.message || "Erro ao criar lançamento.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div className="modal-card">
        <h2>Novo lançamento (conta a pagar)</h2>

        <div className="grid-form novo-lancamento-form">
          <div className="field" style={{}}>
            <input
              name="descricao"
              placeholder="Descrição *"
              value={form.descricao}
              onChange={handleChange}
            />
            <span className="field-help">
              Nome interno para identificar esta conta.
            </span>
          </div>

          <div className="field">
            <input
              name="fornecedorId"
              placeholder="ID Fornecedor (opcional)"
              value={form.fornecedorId}
              onChange={handleChange}
              inputMode="numeric"
            />
            <span className="field-help">
              Código do fornecedor no módulo de cadastros (se houver).
            </span>
          </div>

          <div className="field">
            <select
              name="categoriaId"
              value={form.categoriaId}
              onChange={handleChange}
            >
              <option value="">Categoria *</option>
              {categorias.map((c) => (
                <option key={c.ID_CATEGORIA} value={c.ID_CATEGORIA}>
                  {c.NOME}
                </option>
              ))}
            </select>
            <span className="field-help">
              Usada para relatórios e agrupamento financeiro.
            </span>
          </div>

          <div className="field">
            <select
              name="centroCustoId"
              value={form.centroCustoId}
              onChange={handleChange}
            >
              <option value="">Centro de custo (opcional)</option>
              {centrosCusto.map((cc) => (
                <option key={cc.ID_CC} value={cc.ID_CC}>
                  {cc.NOME}
                </option>
              ))}
            </select>
            <span className="field-help">
              Para qual centro de custo a despesa será lançada.
            </span>
          </div>

          <div className="field">
            <input
              name="tipoDocumento"
              placeholder="Tipo doc. (NF, DUP, etc.)"
              value={form.tipoDocumento}
              onChange={handleChange}
            />
            <span className="field-help">
              Tipo de documento de cobrança (ex.: NF, boleto).
            </span>
          </div>

          <div className="field">
            <input
              name="numDocumento"
              placeholder="Número documento"
              value={form.numDocumento}
              onChange={handleChange}
            />
            <span className="field-help">
              Número da nota, boleto ou duplicata.
            </span>
          </div>

          <div className="field">
            <input
              type="month"
              name="competencia"
              value={form.competencia}
              onChange={handleChange}
            />
            <span className="field-help">
              Mês/ano em que a despesa entra nos relatórios.
            </span>
          </div>

          <div className="field">
            <input
              type="date"
              name="dtVencimento"
              value={form.dtVencimento}
              onChange={handleChange}
            />
            <span className="field-help">
              Data de vencimento (ou da 1ª parcela).
            </span>
          </div>

          <div className="field">
            <input
              name="valor"
              placeholder="Valor (ex: 1234,56) *"
              value={form.valor}
              onChange={handleChange}
              inputMode="decimal"
            />
            <span className="field-help">
              Valor total da conta antes do parcelamento.
            </span>
          </div>

          <div className="field">
            <select
              name="formaPagamento"
              value={form.formaPagamento}
              onChange={handleChange}
            >
              <option value="">Forma de pagamento (opcional)</option>
              {Object.keys(FORMA_LABEL).map((k) => (
                <option key={k} value={k}>
                  {FORMA_LABEL[k]}
                </option>
              ))}
            </select>
            <span className="field-help">
              Forma prevista de pagamento (pode mudar depois).
            </span>
          </div>

          <div className="field" style={{ gridColumn: "span 2" }}>
            <input
              name="observacao"
              placeholder="Observação (opcional)"
              value={form.observacao}
              onChange={handleChange}
            />
            <span className="field-help">
              Qualquer observação adicional sobre o lançamento.
            </span>
          </div>
        </div>

        {/* Configuração de parcelamento */}
        <div
          style={{
            marginTop: "0.75rem",
            padding: "0.75rem 0.9rem",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.85rem",
              marginBottom: parcelamento.parcelado ? 8 : 0,
            }}
          >
            <input
              type="checkbox"
              name="parcelado"
              checked={parcelamento.parcelado}
              onChange={handleParcelamentoChange}
            />
            <span>Conta parcelada</span>
          </label>

          {parcelamento.parcelado && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <input
                  name="qtdParcelas"
                  value={parcelamento.qtdParcelas}
                  onChange={handleParcelamentoChange}
                  placeholder="Qtde de parcelas"
                  inputMode="numeric"
                  style={{
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    padding: "0.4rem 0.8rem",
                    fontSize: "0.8rem",
                    background: "#fff",
                  }}
                />
                <input
                  name="carenciaDias"
                  value={parcelamento.carenciaDias}
                  onChange={handleParcelamentoChange}
                  placeholder="Carência 1ª parcela (dias)"
                  inputMode="numeric"
                  style={{
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    padding: "0.4rem 0.8rem",
                    fontSize: "0.8rem",
                    background: "#fff",
                  }}
                />
                <input
                  name="intervaloDias"
                  value={parcelamento.intervaloDias}
                  onChange={handleParcelamentoChange}
                  placeholder="Intervalo entre parcelas (dias)"
                  inputMode="numeric"
                  style={{
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    padding: "0.4rem 0.8rem",
                    fontSize: "0.8rem",
                    background: "#fff",
                  }}
                />
              </div>
              {parcelasPreview && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                  }}
                >
                  Serão geradas {parcelasPreview.qtd} parcelas de{" "}
                  <strong>{formatMoney(parcelasPreview.valorParcela)}</strong>.
                  A carência e o intervalo serão aplicados a partir da data de
                  vencimento informada.
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button onClick={handleSalvar} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
          <button className="outline" onClick={onFechar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== MODAL PAGAMENTO ===================== */

const PagamentoParcelaModal = ({ aberto, parcela, onFechar, onSalvou }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dtPagamento: todayISO(),
    valorPago: "",
    desconto: "0",
    juros: "0",
    multa: "0",
    formaPagamento: "",
    contaFinanceira: "",
    obs: "",
  });

    useEffect(() => {
    if (aberto && parcela) {
      const isPago = parcela.STATUS === "PAGO";

      if (isPago) {
        const vPago = Number(parcela.VALOR_PAGO || 0);

        setForm({
          dtPagamento:
            (parcela.DT_PAGAMENTO && parcela.DT_PAGAMENTO.slice(0, 10)) ||
            todayISO(),
          valorPago: vPago > 0 ? vPago.toFixed(2).replace(".", ",") : "",
          desconto: (parcela.DESCONTO || 0).toString().replace(".", ","),
          juros: (parcela.JUROS || 0).toString().replace(".", ","),
          multa: (parcela.MULTA || 0).toString().replace(".", ","),
          formaPagamento: parcela.FORMA_PAGAMENTO || "",
          contaFinanceira: parcela.CONTA_FINANCEIRA || "",
          obs: parcela.OBS_PAGAMENTO || "",
        });
      } else {
        const restante =
          Number(parcela.VALOR_PREVISTO || 0) - Number(parcela.VALOR_PAGO || 0);

        setForm({
          dtPagamento: todayISO(),
          valorPago: restante > 0 ? restante.toFixed(2).replace(".", ",") : "",
          desconto: "0",
          juros: "0",
          multa: "0",
          formaPagamento: parcela.FORMA_PAGAMENTO || "",
          contaFinanceira: parcela.CONTA_FINANCEIRA || "",
          obs: "",
        });
      }
    }
  }, [aberto, parcela]);


  if (!aberto || !parcela) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSalvar = async () => {
    const { dtPagamento, valorPago, desconto, juros, multa } = form;
    if (!dtPagamento) {
      showError("Data de pagamento é obrigatória.");
      return;
    }
    const vPago = parseMoney(valorPago);
    if (!vPago || vPago <= 0) {
      showError("Valor pago inválido.");
      return;
    }
    const vDesc = parseMoney(desconto) ?? 0;
    const vJuros = parseMoney(juros) ?? 0;
    const vMulta = parseMoney(multa) ?? 0;

    const payload = {
      dtPagamento,
      valorPago: vPago,
      desconto: vDesc,
      juros: vJuros,
      multa: vMulta,
      formaPagamento: form.formaPagamento || null,
      contaFinanceira: form.contaFinanceira || null,
      obs: form.obs || null,
    };

    setSaving(true);
    try {
      await api.post(
        `/financeiro/contas-pagar/parcelas/${parcela.ID_PARCELA}/pagar`,
        payload
      );
      showSuccess("Pagamento registrado.");
      onSalvou?.();
      onFechar();
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.message;
      if (code === "NAO_PODE_PAGAR_CANCELADA") {
        showError("Não é possível pagar uma parcela cancelada.");
      } else if (code === "JA_PAGA") {
        showError("Parcela já está paga.");
      } else {
        showError("Erro ao registrar pagamento.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div className="modal-card">
        <h2>
          {parcela.STATUS === "PAGO"
            ? `Ajustar pagamento da parcela #${parcela.NUM_PARCELA}`
            : `Pagamento da parcela #${parcela.NUM_PARCELA}`}
        </h2>
        <p>
          <strong>Descrição:</strong> {parcela.DESCRICAO}
          <br />
          <strong>Vencimento:</strong> {formatDate(parcela.DT_VENCIMENTO)} •{" "}
          <strong>Valor previsto:</strong> {formatMoney(parcela.VALOR_PREVISTO)}
          {parcela.STATUS === "PAGO" && (
            <>
              <br />
              <span className="field-help">
                Ajuste os dados abaixo para corrigir valores ou datas já
                registrados. Para desfazer totalmente, use o botão{" "}
                <strong>Estornar</strong> no gerenciador de parcelas.
              </span>
            </>
          )}
        </p>

        <div className="grid-form pagamento-form">
          <div className="field">
            <input
              type="date"
              name="dtPagamento"
              value={form.dtPagamento}
              onChange={handleChange}
            />
            <span className="field-help">
              Data em que o pagamento foi realizado.
            </span>
          </div>

          <div className="field">
            <input
              name="valorPago"
              placeholder="Valor pago"
              value={form.valorPago}
              onChange={handleChange}
              inputMode="decimal"
            />
            <span className="field-help">
              Valor efetivamente pago nesta parcela.
            </span>
          </div>

          <div className="field">
            <input
              name="desconto"
              placeholder="Desconto"
              value={form.desconto}
              onChange={handleChange}
              inputMode="decimal"
            />
            <span className="field-help">
              Desconto concedido, se houver.
            </span>
          </div>

          <div className="field">
            <input
              name="juros"
              placeholder="Juros"
              value={form.juros}
              onChange={handleChange}
              inputMode="decimal"
            />
            <span className="field-help">
              Juros cobrados, se houver.
            </span>
          </div>

          <div className="field">
            <input
              name="multa"
              placeholder="Multa"
              value={form.multa}
              onChange={handleChange}
              inputMode="decimal"
            />
            <span className="field-help">
              Multa por atraso, se houver.
            </span>
          </div>

          <div className="field">
            <select
              name="formaPagamento"
              value={form.formaPagamento}
              onChange={handleChange}
            >
              <option value="">Forma de pagamento</option>
              {Object.keys(FORMA_LABEL).map((k) => (
                <option key={k} value={k}>
                  {FORMA_LABEL[k]}
                </option>
              ))}
            </select>
            <span className="field-help">
              Forma de pagamento utilizada.
            </span>
          </div>

          <div className="field">
            <input
              name="contaFinanceira"
              placeholder="Conta financeira"
              value={form.contaFinanceira}
              onChange={handleChange}
            />
            <span className="field-help">
              Conta / caixa de onde saiu o recurso.
            </span>
          </div>

          <div className="field" style={{ gridColumn: "span 2" }}>
            <input
              name="obs"
              placeholder="Observação (opcional)"
              value={form.obs}
              onChange={handleChange}
            />
            <span className="field-help">
              Qualquer informação adicional sobre este pagamento.
            </span>
          </div>
        </div>


        <div className="modal-actions">
          <button onClick={handleSalvar} disabled={saving}>
            {saving ? "Salvando..." : "Confirmar pagamento"}
          </button>
          <button className="outline" onClick={onFechar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== MODAL GERENCIAR LANÇAMENTO ===================== */

const GerenciarLancamentoModal = ({
  aberto,
  lancamento,
  onFechar,
  onPagarParcela,
  onEstornarParcela,
  onCancelarParcela,
}) => {
  const [sortField, setSortField] = useState("PARCELA");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    if (aberto) {
      setSortField("PARCELA");
      setSortDir("asc");
    }
  }, [aberto]);

  const toggleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return field;
    });
  };

  const sortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortDir === "asc" ? "↑" : "↓";
  };

  const parcelasOrdenadas = useMemo(() => {
    if (!lancamento || !Array.isArray(lancamento.parcelas)) return [];
    const arr = [...lancamento.parcelas];
    const dir = sortDir === "asc" ? 1 : -1;

    return arr.sort((a, b) => {
      switch (sortField) {
        case "STATUS":
          return a.STATUS.localeCompare(b.STATUS) * dir;
        case "VENCIMENTO": {
          const da = a.DT_VENCIMENTO || "";
          const db = b.DT_VENCIMENTO || "";
          return (da > db ? 1 : da < db ? -1 : 0) * dir;
        }
        case "VALOR": {
          const va = Number(a.VALOR_PREVISTO || 0);
          const vb = Number(b.VALOR_PREVISTO || 0);
          return (va - vb) * dir;
        }
        case "PARCELA":
        default: {
          const na = Number(a.NUM_PARCELA || 0);
          const nb = Number(b.NUM_PARCELA || 0);
          return (na - nb) * dir;
        }
      }
    });
  }, [lancamento, sortField, sortDir]);

  if (!aberto || !lancamento) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div className="modal-card">
        <h2>Gerenciar lançamento</h2>
                <p>
          <strong>Código:</strong> {lancamento.codigo} •{" "}
          <strong>Descrição:</strong> {lancamento.descricao}
          <br />
          <span className="field-help">
            Use <strong>Registrar pagamento</strong> para novos pagamentos,
            <strong> Editar pagamento</strong> para corrigir lançamentos já
            pagos e <strong>Estornar</strong> para desfazer totalmente o
            pagamento.
          </span>
        </p>


        <div className="tabela-scroll">
          <table className="tabela-simples">
            <thead>
              <tr>
                <th onClick={() => toggleSort("PARCELA")}>
                  Parcela {sortIcon("PARCELA")}
                </th>
                <th onClick={() => toggleSort("VENCIMENTO")}>
                  Vencimento {sortIcon("VENCIMENTO")}
                </th>
                <th>Competência</th>
                <th onClick={() => toggleSort("VALOR")}>
                  Valor previsto {sortIcon("VALOR")}
                </th>
                <th>Valor pago</th>
                <th onClick={() => toggleSort("STATUS")}>
                  Status {sortIcon("STATUS")}
                </th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {parcelasOrdenadas.map((parc) => (
                <tr key={parc.ID_PARCELA}>
                  <td>{parc.NUM_PARCELA}</td>
                  <td>{formatDate(parc.DT_VENCIMENTO)}</td>
                  <td>{parc.COMPETENCIA}</td>
                  <td>{formatMoney(parc.VALOR_PREVISTO)}</td>
                  <td>{formatMoney(parc.VALOR_PAGO)}</td>
                  <td>{STATUS_LABEL[parc.STATUS] || parc.STATUS}</td>
                                    <td className="col-acoes">
                    {(parc.STATUS === "ABERTO" || parc.STATUS === "PARCIAL") && (
                      <button onClick={() => onPagarParcela(parc)}>
                        Registrar pagamento
                      </button>
                    )}

                    {parc.STATUS === "PAGO" && (
                      <>
                        <button onClick={() => onPagarParcela(parc)}>
                          Editar pagamento
                        </button>
                        {Number(parc.VALOR_PAGO || 0) > 0 && (
                          <button
                            className="outline"
                            onClick={() => onEstornarParcela(parc)}
                          >
                            Estornar
                          </button>
                        )}
                      </>
                    )}

                    {(parc.STATUS === "ABERTO" ||
                      parc.STATUS === "PARCIAL") && (
                      <button
                        className="outline danger"
                        onClick={() => onCancelarParcela(parc)}
                      >
                        Cancelar parcela
                      </button>
                    )}
                  </td>

                </tr>
              ))}

              {!parcelasOrdenadas.length && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    Nenhuma parcela encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="modal-actions">
          <button className="outline" onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== PÁGINA PRINCIPAL ===================== */

const ContasPagar = () => {
  const [parcelas, setParcelas] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [loading, setLoading] = useState(true);

  const [categorias, setCategorias] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);

  const [filtros, setFiltros] = useState({
    status: "",
    categoriaId: "",
    centroCustoId: "",
    dtVencIni: "",
    dtVencFim: "",
    competenciaIni: "",
    competenciaFim: "",
  });
  const [versaoFiltro, setVersaoFiltro] = useState(0);

  const [sortField, setSortField] = useState("PRIMEIRO_VENC");
  const [sortDir, setSortDir] = useState("asc");

  const [showNovoLanc, setShowNovoLanc] = useState(false);
  const [showCategoriasModal, setShowCategoriasModal] = useState(false);
  const [showCCModal, setShowCCModal] = useState(false);
  const [parcelaSelecionada, setParcelaSelecionada] = useState(null);
  const [showPagtoModal, setShowPagtoModal] = useState(false);

  const [showGerenciarModal, setShowGerenciarModal] = useState(false);
  const [lancamentoSelecionado, setLancamentoSelecionado] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const categoriasById = useMemo(() => {
    const m = {};
    categorias.forEach((c) => {
      m[c.ID_CATEGORIA] = c.NOME;
    });
    return m;
  }, [categorias]);

  const ccById = useMemo(() => {
    const m = {};
    centrosCusto.forEach((cc) => {
      m[cc.ID_CC] = cc.NOME;
    });
    return m;
  }, [centrosCusto]);

  const lancamentos = useMemo(
    () => agruparParcelasEmLancamentos(parcelas, categoriasById, ccById),
    [parcelas, categoriasById, ccById]
  );

  const lancamentosOrdenados = useMemo(
    () => sortLancamentos(lancamentos, sortField, sortDir),
    [lancamentos, sortField, sortDir]
  );

  const carregarCategorias = async () => {
    try {
      const { data } = await api.get("/financeiro/contas-pagar/categorias", {
        params: { page: 1, limit: 500 },
      });
      setCategorias(Array.isArray(data?.data) ? data.data : []);
    } catch {
      showError("Erro ao carregar categorias.");
    }
  };

  const carregarCentrosCusto = async () => {
    try {
      const { data } = await api.get(
        "/financeiro/contas-pagar/centros-custo",
        { params: { page: 1, limit: 500 } }
      );
      setCentrosCusto(Array.isArray(data?.data) ? data.data : []);
    } catch {
      showError("Erro ao carregar centros de custo.");
    }
  };

  const carregarParcelas = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };
      if (filtros.status) params.status = filtros.status;
      if (filtros.categoriaId) params.categoriaId = Number(filtros.categoriaId);
      if (filtros.centroCustoId)
        params.centroCustoId = Number(filtros.centroCustoId);
      if (filtros.dtVencIni) params.dtVencIni = filtros.dtVencIni;
      if (filtros.dtVencFim) params.dtVencFim = filtros.dtVencFim;
      if (filtros.competenciaIni)
        params.competenciaIni = filtros.competenciaIni;
      if (filtros.competenciaFim)
        params.competenciaFim = filtros.competenciaFim;

      const { data } = await api.get("/financeiro/contas-pagar", {
        params,
      });
      setParcelas(Array.isArray(data?.data) ? data.data : []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.error(e);
      showError("Erro ao carregar contas a pagar.");
      setParcelas([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCategorias();
    carregarCentrosCusto();
  }, []);

  useEffect(() => {
    carregarParcelas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, versaoFiltro]);

  // re-sincroniza o grupo quando as parcelas mudam e o modal de gerenciar está aberto
  useEffect(() => {
    if (!showGerenciarModal || !lancamentoSelecionado) return;
    const codigo = lancamentoSelecionado.codigo;
    const grupo = parcelas.filter((p) => p.CODIGO === codigo);
    if (!grupo.length) return;
    const base = grupo[0];
    const valorPrev = grupo.reduce(
      (acc, p) => acc + Number(p.VALOR_PREVISTO || 0),
      0
    );
    const valorPago = grupo.reduce(
      (acc, p) => acc + Number(p.VALOR_PAGO || 0),
      0
    );
    setLancamentoSelecionado((prev) => ({
      ...(prev || {}),
      codigo,
      descricao: base.DESCRICAO,
      categoriaNome:
        base.CATEGORIA_NOME || categoriasById[base.ID_CATEGORIA] || "-",
      centroCustoNome: base.CC_NOME || ccById[base.ID_CC] || "-",
      valorTotalPrevisto: valorPrev,
      valorTotalPago: valorPago,
      parcelas: grupo,
    }));
  }, [parcelas, showGerenciarModal, lancamentoSelecionado, categoriasById, ccById]);

  const aplicarFiltros = () => {
    setPage(1);
    setVersaoFiltro((v) => v + 1);
  };

  const limparFiltros = () => {
    setFiltros({
      status: "",
      categoriaId: "",
      centroCustoId: "",
      dtVencIni: "",
      dtVencFim: "",
      competenciaIni: "",
      competenciaFim: "",
    });
    setPage(1);
    setVersaoFiltro((v) => v + 1);
  };

  const abrirPagamento = (parc) => {
    setParcelaSelecionada(parc);
    setShowPagtoModal(true);
  };

  const estornarParcela = async (parc) => {
    if (
      !window.confirm(
        `Confirma estorno do pagamento da parcela #${parc.NUM_PARCELA}?`
      )
    ) {
      return;
    }
    try {
      await api.post(
        `/financeiro/contas-pagar/parcelas/${parc.ID_PARCELA}/estornar`
      );
      showSuccess("Pagamento estornado.");
      carregarParcelas();
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.message;
      if (code === "NAO_PODE_ESTORNAR") {
        showError("Não é possível estornar esta parcela.");
      } else {
        showError("Erro ao estornar pagamento.");
      }
    }
  };

  const cancelarParcela = async (parc) => {
    if (
      !window.confirm(`Confirma cancelar a parcela #${parc.NUM_PARCELA}?`)
    ) {
      return;
    }
    try {
      await api.post(
        `/financeiro/contas-pagar/parcelas/${parc.ID_PARCELA}/cancelar`
      );
      showSuccess("Parcela cancelada.");
      carregarParcelas();
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.message;
      if (code === "NAO_PODE_CANCELAR_PAGA") {
        showError("Não é possível cancelar uma parcela paga.");
      } else {
        showError("Erro ao cancelar parcela.");
      }
    }
  };

  const abrirGerenciarLancamento = (lanc) => {
    if (!lanc) return;
    const codigo = lanc.codigo;
    const grupo = parcelas.filter((p) => p.CODIGO === codigo);
    const valorPrev = grupo.reduce(
      (acc, p) => acc + Number(p.VALOR_PREVISTO || 0),
      0
    );
    const valorPago = grupo.reduce(
      (acc, p) => acc + Number(p.VALOR_PAGO || 0),
      0
    );
    setLancamentoSelecionado({
      codigo,
      descricao: lanc.descricao,
      categoriaNome: lanc.categoriaNome,
      centroCustoNome: lanc.centroCustoNome,
      valorTotalPrevisto: valorPrev,
      valorTotalPago: valorPago,
      parcelas: grupo,
    });
    setShowGerenciarModal(true);
  };

  const toggleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return field;
    });
  };

  return (
    <MainLayout>
      <div className="contas-pagar-container">
        <div className="page-header">
          <div>
            <h1>Contas a Pagar</h1>
            <p>
              Lançamentos, parcelas e gestão de categorias / centros de custo.
            </p>
          </div>
          <div className="act-button">
            <button onClick={() => setShowNovoLanc(true)}>Novo lançamento</button>
            <button onClick={() => setShowCategoriasModal(true)}>Categorias</button>
            <button onClick={() => setShowCCModal(true)}>Centros de custo</button>
          </div>
        </div>

        {/* Filtros */}
        <div className="filtros-card">
          <div className="filtros-header">
            <strong>Filtros</strong>
          </div>
          <div className="filtros-content">
            <div className="grid-filtros">
              <select
                value={filtros.status}
                onChange={(e) =>
                  setFiltros((p) => ({
                    ...p,
                    status: e.target.value,
                  }))
                }
              >
                <option value="">Todos os status</option>
                <option value="ABERTO">Abertos</option>
                <option value="PARCIAL">Parciais</option>
                <option value="PAGO">Pagos</option>
                <option value="CANCELADO">Cancelados</option>
              </select>

              <select
                value={filtros.categoriaId}
                onChange={(e) =>
                  setFiltros((p) => ({
                    ...p,
                    categoriaId: e.target.value,
                  }))
                }
              >
                <option value="">Categoria</option>
                {categorias.map((c) => (
                  <option key={c.ID_CATEGORIA} value={c.ID_CATEGORIA}>
                    {c.NOME}
                  </option>
                ))}
              </select>

              <select
                value={filtros.centroCustoId}
                onChange={(e) =>
                  setFiltros((p) => ({
                    ...p,
                    centroCustoId: e.target.value,
                  }))
                }
              >
                <option value="">Centro de custo</option>
                {centrosCusto.map((cc) => (
                  <option key={cc.ID_CC} value={cc.ID_CC}>
                    {cc.NOME}
                  </option>
                ))}
              </select>

              <div className="inline-range">
                <span>Vencimento:</span>
                <input
                  type="date"
                  value={filtros.dtVencIni}
                  onChange={(e) =>
                    setFiltros((p) => ({
                      ...p,
                      dtVencIni: e.target.value,
                    }))
                  }
                />
                <span>até</span>
                <input
                  type="date"
                  value={filtros.dtVencFim}
                  onChange={(e) =>
                    setFiltros((p) => ({
                      ...p,
                      dtVencFim: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="inline-range">
                <span>Competência:</span>
                <input
                  type="month"
                  value={filtros.competenciaIni}
                  onChange={(e) =>
                    setFiltros((p) => ({
                      ...p,
                      competenciaIni: e.target.value,
                    }))
                  }
                />
                <span>até</span>
                <input
                  type="month"
                  value={filtros.competenciaFim}
                  onChange={(e) =>
                    setFiltros((p) => ({
                      ...p,
                      competenciaFim: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="filtros-actions">
              <button onClick={aplicarFiltros}>Aplicar</button>
              <button className="outline" onClick={limparFiltros}>
                Limpar
              </button>
            </div>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <p>Carregando...</p>
        ) : (
          <>
            <div className="tabela-scroll">
              <table className="tabela-contas-pagar">
                <thead>
                  <tr>
                    <th
                      onClick={() => toggleSort("CODIGO")}
                      style={{ cursor: "pointer" }}
                    >
                      Código
                      <SortIcon active={sortField === "CODIGO"} dir={sortDir} />
                    </th>
                    <th
                      onClick={() => toggleSort("PARCELAS")}
                      style={{ cursor: "pointer" }}
                    >
                      Parcelas
                      <SortIcon active={sortField === "PARCELAS"} dir={sortDir} />
                    </th>
                    <th
                      onClick={() => toggleSort("DESCRICAO")}
                      style={{ cursor: "pointer" }}
                    >
                      Descrição
                      <SortIcon active={sortField === "DESCRICAO"} dir={sortDir} />
                    </th>
                    <th
                      onClick={() => toggleSort("CATEGORIA")}
                      style={{ cursor: "pointer" }}
                    >
                      Categoria
                      <SortIcon active={sortField === "CATEGORIA"} dir={sortDir} />
                    </th>
                    <th
                      onClick={() => toggleSort("CC")}
                      style={{ cursor: "pointer" }}
                    >
                      Centro de Custo
                      <SortIcon active={sortField === "CC"} dir={sortDir} />
                    </th>
                    <th
                      onClick={() => toggleSort("PRIMEIRO_VENC")}
                      style={{ cursor: "pointer" }}
                    >
                      1º Vencimento
                      <SortIcon
                        active={sortField === "PRIMEIRO_VENC"}
                        dir={sortDir}
                      />
                    </th>
                    <th
                      onClick={() => toggleSort("COMPETENCIA")}
                      style={{ cursor: "pointer" }}
                    >
                      Competência
                      <SortIcon
                        active={sortField === "COMPETENCIA"}
                        dir={sortDir}
                      />
                    </th>
                    <th
                      onClick={() => toggleSort("VALOR_PREVISTO")}
                      style={{ cursor: "pointer" }}
                    >
                      Valor Previsto
                      <SortIcon
                        active={sortField === "VALOR_PREVISTO"}
                        dir={sortDir}
                      />
                    </th>
                    <th
                      onClick={() => toggleSort("VALOR_PAGO")}
                      style={{ cursor: "pointer" }}
                    >
                      Valor Pago
                      <SortIcon
                        active={sortField === "VALOR_PAGO"}
                        dir={sortDir}
                      />
                    </th>
                    <th
                      onClick={() => toggleSort("STATUS")}
                      style={{ cursor: "pointer" }}
                    >
                      Status
                      <SortIcon active={sortField === "STATUS"} dir={sortDir} />
                    </th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lancamentosOrdenados.map((l) => (
                    <tr key={l.codigo}>
                      <td>{l.codigo}</td>
                      <td>{l.qtdParcelas}</td>
                      <td>{l.descricao}</td>
                      <td>{l.categoriaNome}</td>
                      <td>{l.centroCustoNome}</td>
                      <td>{formatDate(l.primeiroVencimento)}</td>
                      <td>{l.competenciaLabel || "-"}</td>
                      <td>{formatMoney(l.valorPrevisto)}</td>
                      <td>{formatMoney(l.valorPago)}</td>
                      <td>{STATUS_LABEL[l.status] || l.status}</td>
                      <td className="col-acoes">
                        <button
                          className="outline"
                          onClick={() => abrirGerenciarLancamento(l)}
                        >
                          Gerenciar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!lancamentosOrdenados.length && (
                    <tr>
                      <td colSpan={11} style={{ textAlign: "center" }}>
                        Nenhum registro encontrado com os filtros atuais.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="paginacao">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </button>
              <span>
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Próxima
              </button>
            </div>
          </>
        )}

        {/* MODAIS */}
        <NovoLancamentoModal
          aberto={showNovoLanc}
          onFechar={() => setShowNovoLanc(false)}
          categorias={categorias}
          centrosCusto={centrosCusto}
          onSalvou={() => {
            setPage(1);
            setVersaoFiltro((v) => v + 1);
            carregarCategorias();
            carregarCentrosCusto();
          }}
        />

        <CategoriasModal
          aberto={showCategoriasModal}
          onFechar={() => {
            setShowCategoriasModal(false);
            carregarCategorias();
          }}
        />

        <CentrosCustoModal
          aberto={showCCModal}
          onFechar={() => {
            setShowCCModal(false);
            carregarCentrosCusto();
          }}
        />

        {/* IMPORTANTE: Gerenciar vem ANTES e Pagamento DEPOIS pra Pagamento ficar por cima */}
        <GerenciarLancamentoModal
          aberto={showGerenciarModal}
          lancamento={lancamentoSelecionado}
          onFechar={() => setShowGerenciarModal(false)}
          onPagarParcela={(p) => abrirPagamento(p)}
          onEstornarParcela={(p) => estornarParcela(p)}
          onCancelarParcela={(p) => cancelarParcela(p)}
        />

        <PagamentoParcelaModal
          aberto={showPagtoModal}
          parcela={parcelaSelecionada}
          onFechar={() => setShowPagtoModal(false)}
          onSalvou={() => {
            carregarParcelas();
          }}
        />
      </div>
    </MainLayout>
  );
};

export default ContasPagar;
